import { useState, useEffect, useRef } from 'react';

interface CountdownResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  /** True when no deadline has been set (null target date) */
  hasDeadline: boolean;
  totalSeconds: number;
  formatted: string;
}

/**
 * Convert target date to timestamp number for stable comparison
 */
function getTimestamp(targetDate: Date | string | null): number | null {
  if (!targetDate) return null;
  return typeof targetDate === 'string' ? new Date(targetDate).getTime() : targetDate.getTime();
}

/**
 * Custom hook for live countdown timer
 * Updates every second until the target date
 */
export function useCountdown(targetDate: Date | string | null): CountdownResult {
  // Convert to timestamp for stable comparison
  const targetTimestamp = getTimestamp(targetDate);
  
  // Track the previous timestamp to detect actual changes
  const prevTimestampRef = useRef<number | null>(null);
  
  const [timeLeft, setTimeLeft] = useState<CountdownResult>(() => calculateTimeLeft(targetTimestamp));

  useEffect(() => {
    // Only update if the timestamp actually changed
    if (prevTimestampRef.current === targetTimestamp) {
      return;
    }
    prevTimestampRef.current = targetTimestamp;
    
    if (targetTimestamp === null) {
      setTimeLeft(calculateTimeLeft(null));
      return;
    }

    // Update immediately when target changes
    setTimeLeft(calculateTimeLeft(targetTimestamp));

    // Update every second
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetTimestamp));
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTimestamp]);

  return timeLeft;
}

function calculateTimeLeft(targetTimestamp: number | null): CountdownResult {
  if (targetTimestamp === null) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: false,
      hasDeadline: false,
      totalSeconds: 0,
      formatted: 'TBD',
    };
  }

  const now = Date.now();
  const diffMs = targetTimestamp - now;

  if (diffMs <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
      hasDeadline: true,
      totalSeconds: 0,
      formatted: 'Deadline passed!',
    };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  // Format the countdown string
  let formatted = '';
  if (days > 0) {
    formatted = `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    formatted = `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    formatted = `${minutes}m ${seconds}s`;
  } else {
    formatted = `${seconds}s`;
  }

  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
    hasDeadline: true,
    totalSeconds,
    formatted,
  };
}

